<template>
  <div class="modal is-active">
    <div class="modal-background" @click="handleClose"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">
          <span class="icon mr-2">
            <font-awesome-icon :icon="['fas', iconName]" />
          </span>
          {{ modalTitle }}
        </p>
        <button class="delete" aria-label="close" @click="handleClose"></button>
      </header>
      
      <section class="modal-card-body">
        <div class="feedback-content">
            <div class="field">
              <label class="label">Feedback Type</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select v-model="form.type" @change="onTypeChange">
                    <option value="bug">Bug Report</option>
                    <option value="feature">Feature Request</option>
                  </select>
                </div>
              </div>
            </div>
        
        <div class="field">
          <label class="label">Current Page</label>
          <div class="control">
            <input 
              class="input" 
              type="text" 
              v-model="form.pageUrl" 
              readonly
              disabled
            >
          </div>
          <p class="help">This helps us understand where you encountered the issue</p>
        </div>
        
        <div class="field">
          <label class="label">
            {{ form.type === 'bug' ? 'Describe the bug' : form.type === 'feature' ? 'Describe the feature' : 'Your feedback' }}
          </label>
          <div class="control">
            <textarea 
              class="textarea" 
              v-model="form.message"
              :placeholder="placeholderText"
              rows="6"
              required
            ></textarea>
          </div>
        </div>
        
        <div v-if="form.type === 'bug'" class="field">
          <label class="label">Steps to Reproduce (Optional)</label>
          <div class="control">
            <textarea 
              class="textarea" 
              v-model="form.stepsToReproduce"
              placeholder="1. Go to...
2. Click on...
3. See error..."
              rows="4"
            ></textarea>
          </div>
        </div>
        
        <div v-if="form.type === 'bug'" class="field">
          <label class="label">Expected Behavior (Optional)</label>
          <div class="control">
            <textarea
              class="textarea"
              v-model="form.expectedBehavior"
              placeholder="What did you expect to happen?"
              rows="2"
            ></textarea>
          </div>
        </div>

        <!-- Screenshot Upload (Bug Reports Only) -->
        <div v-if="form.type === 'bug'" class="field">
          <label class="label">Screenshot (Optional)</label>
          <div class="control">
            <div class="file has-name is-fullwidth">
              <label class="file-label">
                <input
                  class="file-input"
                  type="file"
                  accept="image/*"
                  @change="handleScreenshotUpload"
                  ref="fileInput"
                />
                <span class="file-cta">
                  <span class="file-icon">
                    <font-awesome-icon :icon="['fas', 'upload']" />
                  </span>
                  <span class="file-label">Choose image...</span>
                </span>
                <span class="file-name" v-if="screenshotFileName">
                  {{ screenshotFileName }}
                </span>
                <span class="file-name has-text-grey" v-else>
                  No file selected
                </span>
              </label>
            </div>
            <p class="help">Attach a screenshot to help us understand the issue</p>
          </div>
          <!-- Screenshot Preview -->
          <div v-if="screenshotPreview" class="screenshot-preview mt-3">
            <div class="preview-container">
              <img :src="screenshotPreview" alt="Screenshot preview" />
              <button
                class="delete is-small"
                @click="removeScreenshot"
                title="Remove screenshot"
              ></button>
            </div>
          </div>
        </div>

        <div class="field">
          <label class="label">Priority</label>
          <div class="control">
            <div class="select is-fullwidth">
              <select v-model="form.priority">
                <option value="low">Low - Nice to have</option>
                <option value="medium">Medium - Important but not urgent</option>
                <option value="high">High - Urgent issue affecting work</option>
                <option value="critical" v-if="form.type === 'bug'">Critical - Blocking all work</option>
              </select>
            </div>
          </div>
        </div>
        
        <div class="field">
          <label class="label">Browser Information</label>
          <div class="control">
            <input 
              class="input" 
              type="text" 
              :value="browserInfo" 
              readonly
              disabled
            >
          </div>
        </div>
        </div>
      </section>
      
      <footer class="modal-card-foot is-justify-content-flex-end">
        <button class="button is-text" @click="handleClose">Cancel</button>
        <button 
          class="button is-primary" 
          @click="submitFeedback"
          :disabled="!form.message || isSubmitting"
          :class="{ 'is-loading': isSubmitting }"
        >
          Submit Feedback
        </button>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useUserAuthStore } from '@/stores/userAuthStore'
import { useViewModeStore } from '@/stores/viewModeStore'
import { useFeedbackStore } from '@/stores/feedbackStore'
import { feedbackService } from '@/services/feedbackService'
import { consoleInterceptor } from '@/services/consoleInterceptor'
import { useToast } from '@/composables/useToast'
import { logger } from '@/services/loggerService'

const props = defineProps({
  feedbackType: {
    type: String,
    default: 'bug',
    validator: (value) => ['bug', 'feature'].includes(value)
  },
  selectedElement: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close'])

const userAuthStore = useUserAuthStore()
const viewModeStore = useViewModeStore()
const feedbackStore = useFeedbackStore()
const toast = useToast()

const isSubmitting = ref(false)
const fileInput = ref(null)
const screenshotFileName = ref('')
const screenshotFile = ref(null) // Store the actual file for upload
const screenshotPreview = ref(null) // Store base64 preview for display

const form = ref({
  type: props.feedbackType,
  message: '',
  stepsToReproduce: '',
  expectedBehavior: '',
  priority: 'medium',
  pageUrl: '',
  screenshot: null
})

const browserInfo = computed(() => {
  const ua = navigator.userAgent
  let browser = 'Unknown'
  
  if (ua.indexOf('Firefox') > -1) {
    browser = 'Firefox'
  } else if (ua.indexOf('Chrome') > -1) {
    browser = 'Chrome'
  } else if (ua.indexOf('Safari') > -1) {
    browser = 'Safari'
  } else if (ua.indexOf('Edge') > -1) {
    browser = 'Edge'
  }
  
  return `${browser} on ${navigator.platform}`
})

const iconName = computed(() => {
  return form.value.type === 'bug' ? 'bug' : 'lightbulb'
})

const modalTitle = computed(() => {
  return form.value.type === 'bug' ? 'Report a Bug' : 'Request a Feature'
})

const placeholderText = computed(() => {
  return form.value.type === 'bug'
    ? 'Please describe the bug in detail. What went wrong?'
    : 'Please describe the feature you would like to see added.'
})



onMounted(() => {
  form.value.pageUrl = window.location.href
})

const onTypeChange = () => {
  // Clear screenshot when switching to feature request
  if (form.value.type === 'feature') {
    form.value.screenshot = null
    screenshotFileName.value = ''
    if (fileInput.value) {
      fileInput.value.value = ''
    }
  }
}

const handleScreenshotUpload = (event) => {
  const file = event.target.files[0]
  if (!file) return

  // Validate file type
  if (!file.type.startsWith('image/')) {
    toast.error('Please select an image file')
    return
  }

  // Validate file size (max 5MB)
  if (file.size > 5 * 1024 * 1024) {
    toast.error('Screenshot must be less than 5MB')
    return
  }

  screenshotFileName.value = file.name
  screenshotFile.value = file // Store the file for upload

  // Create base64 preview for display only
  const reader = new FileReader()
  reader.onload = (e) => {
    screenshotPreview.value = e.target.result
  }
  reader.onerror = () => {
    toast.error('Failed to read screenshot file')
    screenshotFileName.value = ''
    screenshotFile.value = null
  }
  reader.readAsDataURL(file)
}

const removeScreenshot = () => {
  form.value.screenshot = null
  screenshotFileName.value = ''
  screenshotFile.value = null
  screenshotPreview.value = null
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}



const submitFeedback = async () => {
  if (!form.value.message || isSubmitting.value) return

  isSubmitting.value = true

  try {
    const feedbackData = {
      type: form.value.type || 'bug',
      message: form.value.message || '',
      priority: form.value.priority || 'medium',
      pageUrl: form.value.pageUrl || window.location.href,
      browserInfo: browserInfo.value || 'Unknown',
      userEmail: userAuthStore.user?.email || 'unknown',
      // the store's user object exposes `id`, not `uid` — reading `uid` here
      // silently wrote 'unknown' on every submission
      userId: userAuthStore.user?.id || null,
      userName: userAuthStore.user?.displayName || userAuthStore.user?.email || 'unknown',
      companyId: userAuthStore.user?.companyId || viewModeStore.impersonatedCompanyId || null,
      companyName: userAuthStore.user?.companyName || viewModeStore.impersonatedCompanyName || null,
      isImpersonating: viewModeStore.isImpersonating || false,
      status: 'backlog',
      adminNotes: ''
    }

    // Add console logs for bug reports only
    if (form.value.type === 'bug') {
      const consoleLogs = consoleInterceptor.getConsoleLogs()
      logger.debug('[FeedbackModal] Captured console logs from global interceptor:', { count: consoleLogs.length })
      feedbackData.consoleLogs = consoleLogs
    }

    if (form.value.type === 'bug') {
      if (form.value.stepsToReproduce) {
        feedbackData.stepsToReproduce = form.value.stepsToReproduce
      }
      if (form.value.expectedBehavior) {
        feedbackData.expectedBehavior = form.value.expectedBehavior
      }
      // Upload screenshot to Bunny if present. Store the path as well as the
      // URL — the URL is a 30-day signed link, so the path is what makes the
      // screenshot viewable after it expires.
      if (screenshotFile.value) {
        try {
          const { url, path } = await feedbackService.uploadScreenshot(screenshotFile.value)
          feedbackData.screenshot = url
          feedbackData.screenshotPath = path
        } catch (uploadError) {
          logger.error('[FeedbackModal] Failed to upload screenshot', uploadError)
          toast.warning('Screenshot upload failed, but feedback will still be submitted')
        }
      }
      if (props.selectedElement) {
        feedbackData.selectedElement = {
          tagName: props.selectedElement.tagName || 'unknown',
          id: props.selectedElement.id || '',
          className: props.selectedElement.className || '',
          selector: props.selectedElement.selector || '',
          text: props.selectedElement.text || '',
          xpath: props.selectedElement.xpath || '',
          position: props.selectedElement.position || {},
          scrollPosition: props.selectedElement.scrollPosition || {},
          pageUrl: form.value.pageUrl || window.location.href
        }
      }
    }

    await feedbackStore.submitFeedback(feedbackData)

    // Close modal immediately
    handleClose()

    // Show success toast on the page
    toast.success('Thank you for your feedback! We will review it soon and take appropriate action.')
  } catch (error) {
    logger.error('[FeedbackModal] Error submitting feedback', error)
    toast.error('Failed to submit feedback. Please try again.')
  } finally {
    isSubmitting.value = false
  }
}

const handleClose = () => {
  form.value = {
    type: props.feedbackType || 'bug',
    message: '',
    stepsToReproduce: '',
    expectedBehavior: '',
    priority: 'medium',
    pageUrl: window.location.href,
    screenshot: null
  }
  screenshotFileName.value = ''
  screenshotFile.value = null
  screenshotPreview.value = null
  if (fileInput.value) {
    fileInput.value.value = ''
  }
  emit('close')
}
</script>

<style scoped lang="scss">
.modal-card {
  width: 90%;
  max-width: 600px;
}

.modal-card-body {
  max-height: 70vh;
  overflow-y: auto;
}

.textarea {
  resize: vertical;
}

.screenshot-preview {
  .preview-container {
    position: relative;
    display: inline-block;
    max-width: 100%;

    img {
      max-width: 100%;
      max-height: 200px;
      border-radius: 4px;
      border: 1px solid #dbdbdb;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .delete {
      position: absolute;
      top: -8px;
      right: -8px;
      background: #f14668;
    }
  }
}

.file-label {
  width: 100%;
}

.file-name {
  max-width: none;
  flex: 1;
}
</style>